import heic2any from "heic2any"

/**
 * Checks if a file is a HEIC/HEIF format
 */
export const isHeicFile = (file: File): boolean => {
  const fileName = file.name.toLowerCase()
  const mimeType = file.type.toLowerCase()

  return (
    fileName.endsWith(".heic") ||
    fileName.endsWith(".heif") ||
    mimeType === "image/heic" ||
    mimeType === "image/heif" ||
    mimeType === "image/heic-sequence" ||
    mimeType === "image/heif-sequence"
  )
}

/**
 * Converts a HEIC/HEIF file to JPG/PNG format
 * @param file The HEIC/HEIF file to convert
 * @param outputFormat The desired output format ('jpeg' or 'png'), defaults to 'jpeg'
 * @returns A Promise that resolves to a File object in the requested format
 */
export const convertHeicToImage = async (
  file: File,
  outputFormat: "jpeg" | "png" = "jpeg",
): Promise<File> => {
  try {
    // Convert HEIC to the desired format
    const convertedBlob = await heic2any({
      blob: file,
      toType: `image/${outputFormat}`,
      quality: 0.92, // High quality conversion
    })

    // heic2any can return an array, so we take the first result
    const blob = Array.isArray(convertedBlob) ? convertedBlob[0] : convertedBlob

    // Create a new File object with the converted blob
    const fileName = file.name.replace(/\.(heic|heif)$/i, `.${outputFormat}`)
    const convertedFile = new File([blob], fileName, {
      type: `image/${outputFormat}`,
      lastModified: Date.now(),
    })

    return convertedFile
  } catch (error) {
    console.error("Error converting HEIC file:", error)
    throw new Error(
      `Failed to convert HEIC file: ${error instanceof Error ? error.message : "Unknown error"}`,
    )
  }
}

/**
 * Processes an image file, converting HEIC/HEIF to JPG if needed
 * @param file The image file to process
 * @param outputFormat The desired output format ('jpeg' or 'png'), defaults to 'jpeg'
 * @returns A Promise that resolves to a File object (converted if needed)
 */
export const processImageFile = async (
  file: File,
  outputFormat: "jpeg" | "png" = "jpeg",
): Promise<File> => {
  if (isHeicFile(file)) {
    return convertHeicToImage(file, outputFormat)
  }
  return file
}
